// A data call that leaves the device WITHOUT a login token can only come back
// refused: PostgREST answers as the anonymous role (row security hides every
// row, RPCs get 401) and edge functions answer 401. supabase-js falls back to
// the public key as the bearer the moment it has no stored session, while the
// app can still have a signed-in pilot on screen (the session was announced
// by an auth event, then the browser dropped it).
//
// John Herring, 2026-10-30: his iPhone Chrome kept nothing after a reset link.
// Every page failed quietly as anonymous: "Failed to load profile", feedback
// "returned a non-2xx status code", and no banner anywhere said why. This
// spots the condition at the fetch layer, once, and tells AuthContext, which
// raises the sign-in banner.

export const TOKENLESS_EVENT = "seapa:tokenless-call";

// Only the endpoints that need a login. Auth itself (/auth/v1/...) is
// legitimately called with the public key.
export function isDataCall(url) {
  return /\/(rest|functions|storage)\/v1\//.test(String(url || ""));
}

// The bearer token a fetch() call is about to send, or null.
export function bearerOf(input, init) {
  let raw = null;
  try {
    const h = init?.headers;
    if (h) raw = (h instanceof Headers ? h : new Headers(h)).get("authorization");
    if (!raw && typeof Request !== "undefined" && input instanceof Request) {
      raw = input.headers.get("authorization");
    }
  } catch {
    return null;
  }
  if (!raw) return null;
  const m = /^Bearer\s+(\S+)$/i.exec(raw.trim());
  return m ? m[1] : null;
}

// True when a login-needing call is about to go out with the public key (or
// no bearer at all) instead of a user's token.
export function isTokenlessDataCall(url, bearer, publicKey) {
  if (!isDataCall(url)) return false;
  if (!bearer) return true;
  return bearer === publicKey;
}

const NOTE_EVERY_MS = 5000;
let lastHandledAt = -Infinity;

// Tell the app. A listener that acts on the notice calls preventDefault();
// only then does the five-second quiet window start (a page boot fires
// dozens of calls at once and one acted-on notice is enough). A notice
// nobody could act on yet, e.g. before AuthContext has a user on screen,
// does not start the window, so the next call still gets through (Codex
// on #632). Returns true when the notice was sent.
export function noteTokenlessCall(url, now = Date.now()) {
  if (now - lastHandledAt < NOTE_EVERY_MS) return false;
  try {
    const ev = new CustomEvent(TOKENLESS_EVENT, { detail: { url: String(url || "") }, cancelable: true });
    const unhandled = window.dispatchEvent(ev);
    if (!unhandled) lastHandledAt = now;
  } catch {
    /* no window (tests) — nothing to tell */
  }
  return true;
}

// For code that calls fetch() itself instead of going through the Supabase
// client (the Ask page's streamed answer, slip PDF generation): say so when
// the call is about to go out with the public key.
export function noteIfTokenless(url, bearer, publicKey) {
  if (isTokenlessDataCall(url, bearer, publicKey)) noteTokenlessCall(url);
}

export function resetTokenlessNoteForTests() {
  lastHandledAt = -Infinity;
}
